import { ref, watch } from 'vue';

interface StorageConfig<T> {
  key: string;
  defaultValue: T;
  validator: (value: unknown) => boolean;
  type: 'localStorage' | 'sessionStorage';
}
/**
 * This is a very strict storage solution, making sure, we really have the value validated coming from the storage
 *
 * @param key will be used as key in BrowserStorage
 * @param defaultValue will be the initialValue, when value does not exist in BrowserStorage
 * @param validator validator function to verify, thatBrowserStorage-Value is valid
 * @param type defines the storage-type
 */
export const useBrowserStorage = <T>({ key, defaultValue, validator, type }: StorageConfig<T>) => {
  let targetValue = defaultValue;

  const storage = type === 'sessionStorage' ? sessionStorage : localStorage;

  const storeValue = (value?: T) => {
    if (value === undefined) {
      storage.removeItem(key);
    } else {
      storage.setItem(key, JSON.stringify(value));
    }
  };

  const initialValue = storage.getItem(key);
  if (initialValue !== null) {
    try {
      const candidateValue = JSON.parse(initialValue);

      if (validator(candidateValue)) {
        targetValue = candidateValue;
      } else {
        console.debug(`${type} - initialValue is invalid for "${key}":`, candidateValue);
      }
    } catch (e) {
      console.debug(`${type} - Parsing initialValue failed for "${key}":`, initialValue);
    }
  }

  const variable = ref(targetValue);

  watch(
    () => variable.value,
    (value) => {
      storeValue(value);
    },
    { deep: true, immediate: true },
  );

  return variable;
};
